import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import AdvancedFilterBuilder, { FilterGroup } from './AdvancedFilterBuilder';
import productAnalyticsService from '../../services/api/product-analytics.service';

export interface FunnelStep {
  id: string;
  stepOrder: number;
  eventName: string;
  eventFilters: FilterGroup;
}

export interface FunnelFormData {
  name: string;
  description: string;
  steps: FunnelStep[];
}

interface FunnelBuilderModalProps {
  applicationId: string;
  onClose: () => void;
  onSave: (data: FunnelFormData) => Promise<void>;
  initialData?: FunnelFormData;
}

export default function FunnelBuilderModal({
  applicationId,
  onClose,
  onSave,
  initialData,
}: FunnelBuilderModalProps) {
  const [loading, setLoading] = useState(false);
  const [availableEvents, setAvailableEvents] = useState<string[]>([]);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<FunnelFormData>(
    initialData || {
      name: '',
      description: '',
      steps: [
        {
          id: `step_${Date.now()}_1`,
          stepOrder: 0,
          eventName: '',
          eventFilters: {
            id: 'root',
            logicOperator: 'AND',
            conditions: [],
            groups: [],
          },
        },
        {
          id: `step_${Date.now()}_2`,
          stepOrder: 1,
          eventName: '',
          eventFilters: {
            id: 'root',
            logicOperator: 'AND',
            conditions: [],
            groups: [],
          },
        },
      ],
    }
  );

  // Fetch available events using server-side aggregation (returns ALL distinct event names)
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await productAnalyticsService.getEventsSummary({
          applicationId,
        });

        const uniqueEvents = data.map(e => e.name).sort();

        if (uniqueEvents.length > 0) {
          setAvailableEvents(uniqueEvents);
        } else {
          setAvailableEvents([
            'page_view',
            'button_click',
            'form_submit',
            'purchase',
            'sign_up',
            'login',
            'add_to_cart',
            'checkout',
            'payment_success',
          ]);
        }
      } catch (err) {
        console.error('Failed to fetch events:', err);
        setAvailableEvents([
          'page_view',
          'button_click',
          'form_submit',
          'purchase',
          'sign_up',
          'login',
          'add_to_cart',
          'checkout',
          'payment_success',
        ]);
      }
    };

    if (applicationId) {
      fetchEvents();
    }
  }, [applicationId]);

  // Auto-expand steps with complex filters when editing
  useEffect(() => {
    if (initialData && initialData.steps) {
      const stepsWithFilters = new Set<string>();
      initialData.steps.forEach((step) => {
        // Check if step has complex filters (conditions or nested groups)
        const hasFilters =
          (step.eventFilters.conditions && step.eventFilters.conditions.length > 0) ||
          (step.eventFilters.groups && step.eventFilters.groups.length > 0);

        if (hasFilters) {
          stepsWithFilters.add(step.id);
        }
      });

      if (stepsWithFilters.size > 0) {
        setExpandedSteps(stepsWithFilters);
      }
    }
  }, [initialData]);

  const addStep = () => {
    const newStep: FunnelStep = {
      id: `step_${Date.now()}`,
      stepOrder: formData.steps.length,
      eventName: '',
      eventFilters: {
        id: `root_${Date.now()}`,
        logicOperator: 'AND',
        conditions: [],
        groups: [],
      },
    };

    setFormData({
      ...formData,
      steps: [...formData.steps, newStep],
    });
  };

  const removeStep = (stepId: string) => {
    const filteredSteps = formData.steps.filter(s => s.id !== stepId);
    // Re-order steps
    const reorderedSteps = filteredSteps.map((step, index) => ({
      ...step,
      stepOrder: index,
    }));

    setFormData({
      ...formData,
      steps: reorderedSteps,
    });
  };

  const updateStep = (stepId: string, updates: Partial<FunnelStep>) => {
    setFormData({
      ...formData,
      steps: formData.steps.map(step =>
        step.id === stepId ? { ...step, ...updates } : step
      ),
    });
  };

  const moveStep = (stepId: string, direction: 'up' | 'down') => {
    const index = formData.steps.findIndex(s => s.id === stepId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.steps.length) return;

    const newSteps = [...formData.steps];
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];

    // Update step orders
    const reorderedSteps = newSteps.map((step, i) => ({
      ...step,
      stepOrder: i,
    }));

    setFormData({
      ...formData,
      steps: reorderedSteps,
    });
  };

  const toggleStepExpanded = (stepId: string) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepId)) {
      newExpanded.delete(stepId);
    } else {
      newExpanded.add(stepId);
    }
    setExpandedSteps(newExpanded);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('Please enter a funnel name', {
        duration: 3000,
        icon: '⚠️',
      });
      return;
    }

    if (formData.steps.length < 2) {
      toast.error('A funnel must have at least 2 steps', {
        duration: 3000,
        icon: '⚠️',
      });
      return;
    }

    if (formData.steps.some(s => !s.eventName)) {
      toast.error('Please select an event for all steps', {
        duration: 3000,
        icon: '⚠️',
      });
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      console.error('Failed to save funnel:', err);
      toast.error('Failed to save funnel: ' + (err.message || 'Unknown error'), {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setLoading(false);
    }
  };

  // Get property options from events
  const [propertyOptions, setPropertyOptions] = useState<Array<{ value: string; label: string; type: 'string' | 'number' }>>([]);

  // Fetch real property keys from events
  useEffect(() => {
    const fetchProps = async () => {
      try {
        const keys = await productAnalyticsService.getEventPropertyKeys(applicationId);
        const numericKeys = ['price', 'quantity', 'amount', 'total', 'value', 'count', 'step', 'signup_step', 'rating'];
        setPropertyOptions(keys.map(k => ({
          value: k,
          label: k,
          type: (numericKeys.some(n => k.includes(n)) ? 'number' : 'string') as 'string' | 'number',
        })));
      } catch {
        setPropertyOptions([
          { value: 'page_url', label: 'Page URL', type: 'string' },
          { value: 'page_title', label: 'Page Title', type: 'string' },
        ]);
      }
    };
    if (applicationId) fetchProps();
  }, [applicationId]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg">
                <FunnelIcon className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {initialData ? 'Edit Funnel' : 'Create New Funnel'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Funnel Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Funnel Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Checkout Flow, User Onboarding"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this funnel tracks..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          {/* Funnel Steps */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Funnel Steps * (minimum 2 steps)
              </label>
              <button
                onClick={addStep}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-cyan-600 hover:text-cyan-700 font-medium hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Step</span>
              </button>
            </div>

            <div className="space-y-4">
              {formData.steps.map((step, index) => (
                <div
                  key={step.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  {/* Step Header */}
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700/30">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <select
                          value={step.eventName}
                          onChange={(e) => updateStep(step.id, { eventName: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Select Event</option>
                          {availableEvents.map((event) => (
                            <option key={event} value={event}>
                              {event}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-3">
                      {/* Move Up */}
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            moveStep(step.id, 'up');
                          }}
                          className="p-2 text-white bg-blue-500 hover:bg-blue-600 rounded-lg shadow-sm transition-colors"
                          title="Move step up"
                        >
                          <ChevronUpIcon className="w-4 h-4" />
                        </button>
                      )}

                      {/* Move Down */}
                      {index < formData.steps.length - 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            moveStep(step.id, 'down');
                          }}
                          className="p-2 text-white bg-blue-500 hover:bg-blue-600 rounded-lg shadow-sm transition-colors"
                          title="Move step down"
                        >
                          <ChevronDownIcon className="w-4 h-4" />
                        </button>
                      )}

                      {/* Toggle Filters */}
                      <button
                        type="button"
                        onClick={() => toggleStepExpanded(step.id)}
                        className="px-3 py-1.5 text-xs font-medium text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors"
                      >
                        {expandedSteps.has(step.id) ? 'Hide' : 'Show'} Filters
                      </button>

                      {/* Remove Step */}
                      {formData.steps.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeStep(step.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          title="Remove step"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Step Filters (Expandable) */}
                  {expandedSteps.has(step.id) && (
                    <div className="p-4 bg-white dark:bg-gray-800">
                      <AdvancedFilterBuilder
                        value={step.eventFilters}
                        onChange={(filters) => updateStep(step.id, { eventFilters: filters })}
                        propertyOptions={propertyOptions}
                        placeholder="Select event property"
                        allowNestedGroups={true}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              {loading ? 'Saving...' : initialData ? 'Update Funnel' : 'Create Funnel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
